/**
 * English text for the shared components, under the `ui.*` namespace.
 *
 * This is the source of truth for wording. `fr.ts` must have exactly the same
 * shape — `src/__tests__/i18n.test.ts` fails if the two drift apart.
 *
 * An app overrides any string here by defining the same key in its own message
 * bundle; see `mergeUiMessages` in `./index.ts`.
 */
export const en = {
  ui: {
    common: {
      cancel: 'Cancel',
      continue: 'Continue',
      remove: 'Remove',
      revoke: 'Revoke',
    },

    colorScheme: {
      switchToLight: 'Switch to light mode',
      switchToDark: 'Switch to dark mode',
    },

    password: {
      newLabel: 'New password',
      confirmLabel: 'Confirm new password',
      /** @param min the minimum number of characters */
      hint: 'At least {min} characters. A short sentence is easy to remember and hard to guess.',
      submit: 'Set password',

      error: {
        /** @param min the minimum number of characters */
        tooShort: 'Use at least {min} characters',
        /** @param max the maximum number of characters */
        tooLong: 'Use at most {max} characters',
        mismatch: 'The two passwords do not match',
      },
    },

    /*
     * Why the server refused to set the password, as opposed to `password.error`
     * above, which is what the form works out on its own before asking.
     */
    passwordReset: {
      error: {
        expired:
          'This has taken too long, or the link has already been used. Sign in again to start over.',
        rejected: 'That password was not accepted. Please choose a different one.',
        rateLimited: 'Too many tries. Wait a minute, then try again.',
        server: 'Something went wrong on our end. Please try again.',
        network: 'Could not reach the server. Check your connection and try again.',
      },
    },

    /*
     * Self-serve forgot password (PRX-2033). The flow the user walks: ask for a
     * code, type it, pick an account if the address matches more than one, then
     * set a password.
     */
    forgotPassword: {
      emailTitle: 'Forgot your password?',
      emailHint: 'Enter your email address and we will send you a code.',
      emailLabel: 'Email address',
      emailSubmit: 'Send code',

      codeTitle: 'Check your email',
      /** @param email the address the code was sent to */
      codeHint: 'If {email} is on file, we have sent it a 6-digit code.',
      codeLabel: 'Six-digit code',
      codeSubmit: 'Continue',
      resend: 'Resend code',
      /** @param seconds how long until the button comes back */
      resendIn: 'Resend code in {seconds}s',
      resendExhausted: 'Still no code? Check your spam folder. You can ask again in a few minutes.',
      back: 'Use a different email',

      accountTitle: 'Which account?',
      accountHint: 'More than one account uses this email. Choose the one to reset.',
      /** @param id the account's tenant number, when it has no name to show */
      accountFallback: 'Account {id}',

      passwordTitle: 'Set a new password',

      /*
       * Why the server refused, as opposed to anything the form works out on
       * its own. `badCode` covers every way a code can fail — wrong, expired,
       * already used, or an address with no accounts — because the backend
       * deliberately reports them identically.
       */
      error: {
        badCode: 'That code did not work. Check it and try again, or ask for a new one.',
        rateLimited: 'Too many tries. Wait a minute, then try again.',
        server: 'Something went wrong on our end. Please try again.',
        network: 'Could not reach the server. Check your connection and try again.',
      },
    },

    /*
     * The second-factor step of signing in: type the code, or ask for another
     * one, or pick another way to get it. Enrolling a method is `mfa.enroll`.
     */
    mfaVerify: {
      title: 'Verification Required',
      subtitle: 'Enter the code sent to your device',
      codeLabel: 'Verification code',
      submit: 'Submit',
      trustDevice: 'Trust this device for 30 days',
      resend: "Didn't receive the code? Resend",
      resending: 'Sending...',
      switchMethod: 'Use a different method',
      useBackupCode: 'Use a backup code',
      useAuthCode: 'Use a verification code instead',
      backupCodeLabel: 'Backup code',
      backupCodeHint: 'One of the codes you saved when you set up two-step verification',

      method: {
        totp: 'Authenticator app',
        sms: 'Text message',
        email: 'Email',
      },
    },

    /*
     * Single-use backup codes for a lost phone (PRX-2159). Shown once, when
     * they are made; the security page shows how many are left.
     */
    backupCodes: {
      title: 'Save your backup codes',
      description:
        'If you lose your phone, sign in with one of these codes. Each code works once. Keep them somewhere safe, like a password manager. You will not see them again.',
      listLabel: 'Backup codes',
      copy: 'Copy codes',
      copied: 'Copied',
      download: 'Download codes',
      fileName: 'backup-codes.txt',
      fileTitle: 'Backup codes for two-step verification',
      fileNote: 'Each code works once. Keep this file somewhere safe.',
      done: "I've saved my codes",

      cardTitle: 'Backup codes',
      cardDescription: 'Use a backup code to sign in if you lose your phone.',
      /** @param count how many unused codes are left */
      remaining: 'No codes left | 1 code left | {count} codes left',
      lowWarning: 'You are running out of backup codes. Make a new set.',
      regenerate: 'Make new codes',
      regenerateConfirm: {
        header: 'Make new backup codes?',
        message: 'Your old backup codes will stop working.',
        accept: 'Make new codes',
      },
      regenerateFailed: 'Could not make new backup codes. Try again.',
    },

    mfaSetup: {
      title: 'Set up two-step verification',
      subtitle: 'Your account needs a second step to sign in. Choose how to get your codes.',
      totpTitle: 'Authenticator app',
      totpDescription: 'Use Google Authenticator, Authy or 1Password. Works without phone signal.',
      smsTitle: 'Text message',
      smsDescription: 'Get a code by text message',
      smsLink: "Can't use an app? Get codes by text",
      recommended: 'Recommended',
      phoneLabel: 'Phone number',
      phoneHint: 'Include the country code, for example +15551234567',
      sendCode: 'Send code',
      scanPrompt: 'Scan this QR code with your authenticator app',
      manualKeyPrompt: "Can't scan it? Enter this key:",
      smsCodePrompt: 'Enter the 6-digit code we sent to your phone',
      codeLabel: 'Verification code',
      submit: 'Verify and continue',
      resend: 'Send a new code',
      useDifferentMethod: 'Use a different method',

      error: {
        invalidCode: "That code didn't work. Try again.",
        invalidPhone: 'Enter the phone number with its country code, for example +15551234567.',
        expired: 'Your setup time ran out. Sign in again to continue.',
        locked: 'Too many attempts. Wait a few minutes, then sign in again.',
        signInAgain: 'Your MFA is set up. Sign in again to continue.',
        rateLimited: 'Too many attempts. Wait a minute and try again.',
        network: "We couldn't reach the server. Check your connection and try again.",
        server: 'Something went wrong. Try again.',
      },
    },

    mfa: {
      title: 'Multi-Factor Authentication',
      addMethod: 'Add Method',
      authenticatorApp: 'Authenticator App',
      /** @param last4 last four digits of the phone number */
      smsMasked: 'SMS (•••• {last4})',
      preferredBadge: 'PREFERRED',
      setPreferred: 'Set Preferred',

      empty: {
        title: 'No MFA methods configured',
        description: 'Add an authenticator app or SMS verification to secure your account',
      },

      removeConfirm: {
        header: 'Remove MFA Method',
        message: 'This method will no longer be available for login verification. Continue?',
      },

      notify: {
        preferredUpdated: 'Preferred method updated',
        preferredFailed: 'Failed to update preferred method',
        removed: 'MFA method removed',
        removeFailed: 'Failed to remove MFA method',
        added: 'MFA method added successfully',
      },

      enroll: {
        header: 'Add MFA Method',
        choosePrompt: "Choose how you'd like to verify your identity",
        recommendedBadge: 'RECOMMENDED',
        totpDescription: 'Use Google Authenticator, Authy, or 1Password',
        smsTitle: 'SMS Verification',
        smsDescription: 'Receive a code via text message',
        phonePrompt: 'Enter your phone number to receive verification codes',
        phoneLabel: 'Phone number',
        phonePlaceholder: '+1 (555) 123-4567',
        scanPrompt: 'Scan this QR code with your authenticator app',
        manualKeyPrompt: "Can't scan? Enter this key manually:",
        totpCodeLabel: 'Enter the 6-digit code from your app',
        smsCodePrompt: 'Enter the 6-digit code sent to your phone',
        resendCode: 'Resend code',
        sendCode: 'Send Code',
        verifyAndEnable: 'Verify & Enable',
      },

      recommended: {
        bannerMessage: 'Your account is not protected by multi-factor authentication.',
        bannerSuggestion: 'We recommend setting up an authenticator app.',
        bannerAction: 'Set up MFA',
        dialogTitle: 'Secure your account',
        dialogBody:
          'Multi-factor authentication adds an extra layer of protection. We recommend setting up an authenticator app.',
        dialogSetup: 'Set up MFA now',
        dialogLater: 'Remind me later',
      },
    },

    trustedDevices: {
      title: 'Trusted Devices',
      revokeAll: 'Revoke All',
      empty: 'No trusted devices. Devices you trust during MFA login will appear here.',
      unknownDevice: 'Unknown Device',
      thisDevice: 'This device',
      /** @param date already-formatted date */
      lastUsed: 'Last used: {date}',
      /** @param date already-formatted date */
      expires: 'Expires: {date}',

      revokeAllConfirm: {
        header: 'Revoke All Devices',
        message: 'All devices will need to complete MFA verification on next login. Continue?',
      },

      revokeConfirm: {
        header: 'Revoke Trusted Device',
        /** @param device the device's friendly name */
        message: "Revoke trust for {device}? You'll need to verify MFA again on this device.",
      },

      notify: {
        revoked: 'Device revoked',
        revokeFailed: 'Failed to revoke device',
        allRevoked: 'All devices revoked',
        revokeAllFailed: 'Failed to revoke devices',
      },

      date: {
        today: 'Today',
        yesterday: 'Yesterday',
      },
    },
  },
};
